import path from 'path'

import type { FlattenedField } from '../fields/config/types.js'

import { APIError } from '../errors/APIError.js'

type Operation = 'create' | 'update'

export type ExternalUploadSource = {
  filename: string
  url: string
}

type UploadDataOptions = {
  locale?: string
  localizedProperties?: Set<string>
}

const hasOwnProperty = (value: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const getExternalUploadSource = (data: unknown): ExternalUploadSource | undefined => {
  if (!isRecord(data) || typeof data.filename !== 'string' || typeof data.url !== 'string') {
    return undefined
  }

  return {
    filename: data.filename,
    url: data.url,
  }
}

// Resolves the server-owned upload destination for a document write. For client uploads the prefix
// and key segment come from the verified upload context; for server uploads the prefix falls back
// to the submitted document data and there is no key segment.
export const getUploadDestination = ({
  data,
  file,
}: {
  data: unknown
  file: { clientUploadContext?: unknown } | null | undefined
}): { objectKey?: string; prefix?: string } => {
  if (!file) {
    return {}
  }

  const submittedPrefix =
    isRecord(data) && typeof data.prefix === 'string' ? data.prefix : undefined
  const { clientUploadContext } = file

  if (!isRecord(clientUploadContext)) {
    return { prefix: submittedPrefix }
  }

  return {
    objectKey:
      typeof clientUploadContext._objectKey === 'string'
        ? clientUploadContext._objectKey
        : undefined,
    prefix: typeof clientUploadContext.prefix === 'string' ? clientUploadContext.prefix : undefined,
  }
}

const getDocumentProperty = (
  document: Record<string, unknown>,
  property: string,
  { locale, localizedProperties }: UploadDataOptions,
): unknown => {
  const value = document[property]
  return locale && localizedProperties?.has(property) && isRecord(value) ? value[locale] : value
}

// Accepts flattened fields so localized identity fields declared inside presentational
// containers (row/collapsible/unnamed tabs) are still detected — those live at the document
// root data level but are nested in the config tree, so walking `fields` alone would miss them.
export const getLocalizedUploadProperties = (fields: FlattenedField[]): Set<string> => {
  const localizedProperties = new Set<string>()

  for (const field of fields) {
    if ('name' in field && (field as { localized?: boolean }).localized) {
      localizedProperties.add(field.name)
    }
  }

  return localizedProperties
}

// The submitted filename is stripped by sanitizeUploadData regardless, so this only exists to
// reject path-traversal attempts loudly (400) instead of silently dropping them. Non-string
// values are ignored here because they are removed by the caller anyway.
const validateFilename = (filename: unknown): void => {
  if (typeof filename !== 'string') {
    return
  }

  const pathSegments = filename.replace(/\\/g, '/').split('/')

  if (
    path.posix.isAbsolute(filename) ||
    path.win32.isAbsolute(filename) ||
    pathSegments.includes('..')
  ) {
    throw new APIError('Invalid filename.', 400)
  }
}

export const sanitizeUploadData = <T>(data: T, operation: Operation): T => {
  if (!isRecord(data)) {
    return data
  }

  validateFilename(data.filename)

  if (isRecord(data.sizes)) {
    for (const sizeData of Object.values(data.sizes)) {
      if (isRecord(sizeData)) {
        validateFilename(sizeData.filename)
      }
    }
  }

  const sanitizedData: Record<string, unknown> = { ...data }
  delete sanitizedData.filename
  delete sanitizedData.sizes
  delete sanitizedData.url
  // Server-owned; never accepted from the caller.
  delete sanitizedData._objectKey

  // On update, `prefix` is restored from the stored document (file identity); kept on create.
  if (operation === 'update') {
    delete sanitizedData.prefix
  }

  return sanitizedData as T
}

// Restores only the identity fields that select/serve a stored object (`filename`, `prefix`,
// `url`, `sizes`). Other generated-but-derived fields (`thumbnailURL`, `filesize`, `height`,
// `width`, `mimeType`, `focalX`/`focalY`) are intentionally left as submitted: `focalX`/`focalY`
// are user-editable crop input, and the rest are regenerated by generateFileData when a file is
// written. Compare with restoreUploadDataFromDocument (version restore), which re-binds the full
// tuple below.
export const mergeUploadDataWithDocument = <T>(
  data: T,
  document: unknown,
  options: UploadDataOptions = {},
): T => {
  if (!isRecord(data) || !isRecord(document)) {
    return data
  }

  const mergedData: Record<string, unknown> = { ...data }

  for (const property of ['_objectKey', 'filename', 'prefix', 'url']) {
    if (!hasOwnProperty(data, property) && hasOwnProperty(document, property)) {
      mergedData[property] = getDocumentProperty(document, property, options)
    }
  }

  const documentSizes = getDocumentProperty(document, 'sizes', options)
  if (!hasOwnProperty(data, 'sizes') && hasOwnProperty(document, 'sizes')) {
    mergedData.sizes = documentSizes
  } else if (isRecord(data.sizes) && isRecord(documentSizes)) {
    const mergedSizes: Record<string, unknown> = { ...documentSizes }

    for (const [sizeName, sizeData] of Object.entries(data.sizes)) {
      const documentSize = documentSizes[sizeName]

      mergedSizes[sizeName] =
        isRecord(sizeData) && isRecord(documentSize) ? { ...documentSize, ...sizeData } : sizeData
    }

    mergedData.sizes = mergedSizes
  }

  return mergedData as T
}

const uploadDerivedProperties = [
  '_objectKey',
  'filename',
  'filesize',
  'focalX',
  'focalY',
  'height',
  'mimeType',
  'prefix',
  'sizes',
  'thumbnailURL',
  'url',
  'width',
] as const

export const restoreUploadDataFromDocument = <T>(
  data: T,
  document: unknown,
  options: UploadDataOptions = {},
): T => {
  if (!isRecord(data) || !isRecord(document)) {
    return data
  }

  const restoredData: Record<string, unknown> = { ...data }
  for (const property of uploadDerivedProperties) {
    if (hasOwnProperty(document, property)) {
      restoredData[property] = getDocumentProperty(document, property, options)
    } else {
      delete restoredData[property]
    }
  }

  return restoredData as T
}
